import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Divider,
  Pagination,
  ListSubheader,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Dashboard as DashboardIcon,
} from '@mui/icons-material';
import { firmService, Firm } from '../services/firmService';
import { useAuth } from '../contexts/AuthContext';
import {  useNavigate } from 'react-router-dom';
import {
  COUNTRIES,
  INDIAN_STATES,
  US_STATES,
  getCountryOptions,
  getPostalCodeLabel,
  getStateOptions,
  hasPresetStates,
  normalizeCountry,
} from '../utils/locations';

const FILTER_DEBOUNCE_MS = 300;

const Firms: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [firms, setFirms] = useState<Firm[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Check if user is a vendor user
  const isVendorUser = () => {
    if (!user?.roles) return false;
    const vendorRoleSlugs = ['vendor_staff', 'vendor_admin'];
    return user.roles.some(role => vendorRoleSlugs.includes(role.slug));
  };
  
  const [filter, setFilter] = useState<'all' | 'client' | 'vendor'>('client');
  const [listTab, setListTab] = useState<'clients' | 'vendors' | 'suspended'>('clients');
  const [statusFilter, setStatusFilter] = useState<'all' | 'approved' | 'pending' | 'rejected'>('approved');
  const [nameSearch, setNameSearch] = useState('');
  const [debouncedNameSearch, setDebouncedNameSearch] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(15);
  const [total, setTotal] = useState(0);
  const [lastPage, setLastPage] = useState(1);
  
  // Create firm dialog
  const [createDialog, setCreateDialog] = useState<{
    open: boolean;
    formData: {
      name: string;
      type: 'client' | 'vendor';
      address_1: string;
      address_2: string;
      zip_code: string;
      state: string;
      city: string;
      country: string;
      contact_phone: string;
      website: string;
      client_admin_email: string;
    };
  }>({
    open: false,
    formData: {
      name: '',
      type: 'client',
      address_1: '',
      address_2: '',
      zip_code: '',
      state: '',
      city: '',
      country: 'United States',
      contact_phone: '',
      website: '',
      client_admin_email: '',
    },
  });

  // Edit firm dialog
  const [editDialog, setEditDialog] = useState<{
    open: boolean;
    firm: Firm | null;
    formData: any;
  }>({
    open: false,
    firm: null,
    formData: {},
  });

  const [actionLoading, setActionLoading] = useState<number | null>(null);

  const createStateOptions = getStateOptions(createDialog.formData.country, createDialog.formData.state);
  const editStateOptions = getStateOptions(editDialog.formData.country, editDialog.formData.state);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedNameSearch(nameSearch), FILTER_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [nameSearch]);

  useEffect(() => {
    setPage(1);
  }, [filter, listTab, statusFilter, debouncedNameSearch, stateFilter]);

  useEffect(() => {
    fetchFirms();
  }, [page, perPage, filter, listTab, statusFilter, debouncedNameSearch, stateFilter]);

  // Set default filter based on user type when user data is available
  useEffect(() => {
    if (user && isVendorUser()) {
      setFilter('vendor');
    }
  }, [user]);

  const fetchFirms = async () => {
    try {
      setLoading(true);
      const params: Record<string, string | number> = {
        page,
        per_page: perPage,
      };
      if (isVendorUser()) {
        if (filter !== 'all') params.type = filter;
      } else if (listTab === 'suspended') {
        params.access = 'suspended';
        params.type = 'client';
      } else if (listTab === 'vendors') {
        params.type = 'vendor';
        params.access = 'active';
      } else {
        params.type = 'client';
        params.access = 'active';
      }
      if (statusFilter !== 'all') params.status = statusFilter;
      if (debouncedNameSearch.trim()) params.search = debouncedNameSearch.trim();
      if (stateFilter) params.state = stateFilter;

      const response = await firmService.getAllFirms(params);
      setFirms(response.data.data);
      setTotal(response.data.total);
      setLastPage(response.data.last_page || 1);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFirm = async () => {
    try {
      setActionLoading(-1); // Use -1 for create action
      await firmService.createFirm(createDialog.formData);
      setSuccess('Firm created successfully');
      setCreateDialog({ open: false, formData: { ...createDialog.formData, name: '', address_1: '', address_2: '', zip_code: '', state: '', city: '', contact_phone: '', website: '', client_admin_email: '' } });
      fetchFirms();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleUpdateFirm = async () => {
    if (!editDialog.firm) return;

    try {
      setActionLoading(editDialog.firm.id);
      await firmService.updateFirm(editDialog.firm.id, editDialog.formData);
      setSuccess('Firm updated successfully');
      setEditDialog({ open: false, firm: null, formData: {} });
      fetchFirms();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteFirm = async (firmId: number) => {
    if (!window.confirm('Are you sure you want to delete this firm?')) return;

    try {
      setActionLoading(firmId);
      await firmService.deleteFirm(firmId);
      setSuccess('Firm deleted successfully');
      fetchFirms();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusChip = (status: string) => {
    switch (status) {
      case 'pending':
        return <Chip label="Pending" color="warning" size="small" />;
      case 'approved':
        return <Chip label="Approved" color="success" size="small" />;
      case 'rejected':
        return <Chip label="Rejected" color="error" size="small" />;
      default:
        return <Chip label={status} size="small" />;
    }
  };

  const hasOpenDownloadAccess = (firm: Firm) =>
    firm.access_mode === 'download_only' &&
    !!firm.download_access_until &&
    new Date(firm.download_access_until) > new Date();

  const getTypeChip = (type: string) => {
    switch (type) {
      case 'client':
        return <Chip label="Client" color="primary" size="small" />;
      case 'vendor':
        return <Chip label="Vendor" color="secondary" size="small" />;
      default:
        return <Chip label={type} size="small" />;
    }
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const handlePerPageChange = (event: any) => {
    setPerPage(Number(event.target.value));
    setPage(1);
  };

  const isPmpAdmin = () => {
    if (!user?.roles) return false;
    return user.roles.some(role => role.slug === 'pmp_admin');
  };

  const isSiteAdmin = () => {
    if (!user?.roles) return false;
    return user.roles.some(role => role.slug === 'site_admin');
  };

  const isPmpAdminOrSiteAdmin = () => {
    return isPmpAdmin() || isSiteAdmin();
  };

  return (
    <Box>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', sm: 'center' }, 
        mb: 3,
        gap: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box>
            <Typography variant="h4" gutterBottom>
              Firms
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
              Manage client and vendor firms
            </Typography>
          </Box>
        </Box>
        {isPmpAdminOrSiteAdmin() && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialog({ ...createDialog, open: true })}
            fullWidth={false}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            Create Firm
          </Button>
        )}
      </Box>

      <Box>
        <Button
          variant="outlined"
          startIcon={<DashboardIcon />}
          onClick={() => navigate('/')}
          sx={{ 
            borderColor: 'primary.main',
            '&:hover': {
              borderColor: 'primary.dark',
            }
          }}
        >
          Dashboard
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {!isVendorUser() && (
        <Tabs
          value={listTab}
          onChange={(_, value) => setListTab(value)}
          sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab value="clients" label="Clients" />
          <Tab value="vendors" label="Vendors" />
          {isPmpAdminOrSiteAdmin() && <Tab value="suspended" label="Suspended" />}
        </Tabs>
      )}

      {/* Filters */}
      <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'stretch', sm: 'center' } }}>
          <Typography variant="subtitle2" sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}>Filters:</Typography>

          {isVendorUser() && (
            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 120 } }}>
              <InputLabel>Type</InputLabel>
              <Select
                value={filter}
                label="Type"
                onChange={(e) => setFilter(e.target.value as 'all' | 'client' | 'vendor')}
              >
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="client">Clients</MenuItem>
                <MenuItem value="vendor">Vendors</MenuItem>
              </Select>
            </FormControl>
          )}

          {isPmpAdminOrSiteAdmin() && (
            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 120 } }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value as 'all' | 'approved' | 'pending' | 'rejected')}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </Select>
            </FormControl>
          )}

          <TextField
            size="small"
            label="Search firm name"
            value={nameSearch}
            onChange={(e) => setNameSearch(e.target.value)}
            sx={{ minWidth: { xs: '100%', sm: 200 } }}
          />

          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 160 } }}>
            <InputLabel>State</InputLabel>
            <Select
              value={stateFilter}
              label="State"
              onChange={(e) => setStateFilter(e.target.value)}
            >
              <MenuItem value="">All States</MenuItem>
              <ListSubheader disableSticky>United States</ListSubheader>
              {US_STATES.map((state) => (
                <MenuItem key={`us-${state}`} value={state}>{state}</MenuItem>
              ))}
              <ListSubheader disableSticky>India</ListSubheader>
              {INDIAN_STATES.map((state) => (
                <MenuItem key={`in-${state}`} value={state}>{state}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Typography variant="body2" color="text.secondary" sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}>
            {total > 0
              ? `Showing ${(page - 1) * perPage + 1} to ${Math.min(page * perPage, total)} of ${total} firms`
              : 'No firms found'}
          </Typography>
        </Box>
      </Paper>

      {loading ? (
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      ) : firms.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            {listTab === 'suspended' ? 'No suspended firms' : 'No firms found matching the current filters'}
          </Typography>
        </Paper>
      ) : (
        <TableContainer 
          component={Paper}
          sx={{
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          <Table size="small" sx={{ minWidth: 800 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Organization</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Type</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Status</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', display: { xs: 'none', md: 'table-cell' } }}>Contact</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', display: { xs: 'none', lg: 'table-cell' } }}>Location</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', display: { xs: 'none', md: 'table-cell' } }}>Registration Date</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {firms.map((firm) => (
                <TableRow key={firm.id}>
                  <TableCell>
                    <Typography 
                      variant="subtitle2" 
                      fontWeight="bold"
                      sx={{ 
                        cursor: 'pointer', 
                        color: 'primary.main',
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        '&:hover': { textDecoration: 'underline' }
                      }}
                      onClick={() => navigate(`/firms/${firm.id}`)}
                    >
                      {firm.name}
                    </Typography>
                    {firm.website && (
                      <Typography variant="caption" color="text.secondary">
                        {firm.website}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {getTypeChip(firm.type)}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {getStatusChip(firm.status)}
                      {hasOpenDownloadAccess(firm) ? (
                        <Chip
                          label={`Download only until ${new Date(firm.download_access_until as string).toLocaleString()}`}
                          color="warning"
                          size="small"
                        />
                      ) : (firm.access_mode === 'ceased' || firm.category === 'suspended' || firm.access_mode === 'download_only') && (
                        <Chip label="Suspended" color="error" size="small" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                    <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      {firm.registration_data?.full_name || 'N/A'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {firm.registration_data?.email || firm.contact_phone || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                    <Typography variant="body2">
                      {firm.city}, {firm.state}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {firm.country}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                    {new Date(firm.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 1 }, flexWrap: 'wrap' }}>
                      {isPmpAdmin() && (
                        <>
                          <Tooltip title="Edit Firm">
                            <IconButton
                              size="small"
                              onClick={() => setEditDialog({
                                open: true,
                                firm,
                                formData: {
                                  name: firm.name,
                                  type: firm.type,
                                  address_1: firm.address_1,
                                  address_2: firm.address_2,
                                  zip_code: firm.zip_code,
                                  state: firm.state,
                                  city: firm.city,
                                  country: normalizeCountry(firm.country) || firm.country,
                                  contact_phone: firm.contact_phone,
                                  website: firm.website,
                                }
                              })}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Firm">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteFirm(firm.id)}
                              disabled={actionLoading === firm.id}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!loading && total > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3, flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Per Page</InputLabel>
              <Select value={perPage} label="Per Page" onChange={handlePerPageChange}>
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={15}>15</MenuItem>
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Pagination
            count={lastPage}
            page={page}
            onChange={handlePageChange}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Box>
      )}

      {/* Create Firm Dialog */}
      <Dialog
        open={createDialog.open}
        onClose={() => setCreateDialog({ ...createDialog, open: false })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Create New Firm</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                fullWidth
                label="Organization Name *"
                value={createDialog.formData.name}
                onChange={(e) => setCreateDialog({
                  ...createDialog,
                  formData: { ...createDialog.formData, name: e.target.value }
                })}
              />
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <FormControl sx={{ flex: '1 1 300px' }}>
                  <InputLabel>Firm Type *</InputLabel>
                  <Select
                    value={createDialog.formData.type}
                    label="Firm Type *"
                    onChange={(e) => setCreateDialog({
                      ...createDialog,
                      formData: { ...createDialog.formData, type: e.target.value as 'client' | 'vendor' }
                    })}
                  >
                    <MenuItem value="client">Client</MenuItem>
                    <MenuItem value="vendor">Vendor</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  sx={{ flex: '1 1 300px' }}
                  label="Contact Phone"
                  value={createDialog.formData.contact_phone}
                  onChange={(e) => setCreateDialog({
                    ...createDialog,
                    formData: { ...createDialog.formData, contact_phone: e.target.value }
                  })}
                />
              </Box>
              <TextField
                fullWidth
                label="Website"
                value={createDialog.formData.website}
                onChange={(e) => setCreateDialog({
                  ...createDialog,
                  formData: { ...createDialog.formData, website: e.target.value }
                })}
              />
              <TextField
                fullWidth
                label="Address 1 *"
                value={createDialog.formData.address_1}
                onChange={(e) => setCreateDialog({
                  ...createDialog,
                  formData: { ...createDialog.formData, address_1: e.target.value }
                })}
              />
              <TextField
                fullWidth
                label="Address 2"
                value={createDialog.formData.address_2}
                onChange={(e) => setCreateDialog({
                  ...createDialog,
                  formData: { ...createDialog.formData, address_2: e.target.value }
                })}
              />
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                  sx={{ flex: '1 1 300px' }}
                  select
                  label="Country *"
                  value={createDialog.formData.country}
                  onChange={(e) => setCreateDialog({
                    ...createDialog,
                    formData: { ...createDialog.formData, country: e.target.value, state: '' }
                  })}
                >
                  {COUNTRIES.map((country) => (
                    <MenuItem key={country} value={country}>
                      {country}
                    </MenuItem>
                  ))}
                </TextField>
                {hasPresetStates(createDialog.formData.country) ? (
                  <TextField
                    sx={{ flex: '1 1 300px' }}
                    select
                    label="State *"
                    value={createDialog.formData.state}
                    onChange={(e) => setCreateDialog({
                      ...createDialog,
                      formData: { ...createDialog.formData, state: e.target.value }
                    })}
                  >
                    <MenuItem value="">
                      <em>- Select -</em>
                    </MenuItem>
                    {createStateOptions.map((state) => (
                      <MenuItem key={state} value={state}>
                        {state}
                      </MenuItem>
                    ))}
                  </TextField>
                ) : (
                  <TextField
                    sx={{ flex: '1 1 300px' }}
                    label="State *"
                    value={createDialog.formData.state}
                    onChange={(e) => setCreateDialog({
                      ...createDialog,
                      formData: { ...createDialog.formData, state: e.target.value }
                    })}
                  />
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                  sx={{ flex: '1 1 300px' }}
                  label="City *"
                  value={createDialog.formData.city}
                  onChange={(e) => setCreateDialog({
                    ...createDialog,
                    formData: { ...createDialog.formData, city: e.target.value }
                  })}
                />
                <TextField
                  sx={{ flex: '1 1 300px' }}
                  label={`${getPostalCodeLabel(createDialog.formData.country)} *`}
                  value={createDialog.formData.zip_code}
                  onChange={(e) => setCreateDialog({
                    ...createDialog,
                    formData: { ...createDialog.formData, zip_code: e.target.value }
                  })}
                />
              </Box>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" color="text.secondary">
                Client Admin Email (Optional)
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                If provided, an approval email will be sent to this address when the firm is approved.
              </Typography>
              <TextField
                fullWidth
                type="email"
                label="Client Admin Email"
                value={createDialog.formData.client_admin_email}
                onChange={(e) => setCreateDialog({
                  ...createDialog,
                  formData: { ...createDialog.formData, client_admin_email: e.target.value }
                })}
                placeholder="admin@example.com"
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setCreateDialog({ ...createDialog, open: false })}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateFirm}
            variant="contained"
            disabled={actionLoading === -1 || !createDialog.formData.name || !createDialog.formData.address_1 || !createDialog.formData.city || !createDialog.formData.state || !createDialog.formData.zip_code}
          >
            {actionLoading === -1 ? <CircularProgress size={16} /> : 'Create Firm'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Firm Dialog */}
      <Dialog
        open={editDialog.open}
        onClose={() => setEditDialog({ open: false, firm: null, formData: {} })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Firm</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                fullWidth
                label="Organization Name *"
                value={editDialog.formData.name || ''}
                onChange={(e) => setEditDialog({
                  ...editDialog,
                  formData: { ...editDialog.formData, name: e.target.value }
                })}
              />
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <FormControl sx={{ flex: '1 1 300px' }}>
                  <InputLabel>Firm Type *</InputLabel>
                  <Select
                    value={editDialog.formData.type || 'client'}
                    label="Firm Type *"
                    onChange={(e) => setEditDialog({
                      ...editDialog,
                      formData: { ...editDialog.formData, type: e.target.value as 'client' | 'vendor' }
                    })}
                  >
                    <MenuItem value="client">Client</MenuItem>
                    <MenuItem value="vendor">Vendor</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  sx={{ flex: '1 1 300px' }}
                  label="Contact Phone"
                  value={editDialog.formData.contact_phone || ''}
                  onChange={(e) => setEditDialog({
                    ...editDialog,
                    formData: { ...editDialog.formData, contact_phone: e.target.value }
                  })}
                />
              </Box>
              <TextField
                fullWidth
                label="Website"
                value={editDialog.formData.website || ''}
                onChange={(e) => setEditDialog({
                  ...editDialog,
                  formData: { ...editDialog.formData, website: e.target.value }
                })}
              />
              <TextField
                fullWidth
                label="Address 1 *"
                value={editDialog.formData.address_1 || ''}
                onChange={(e) => setEditDialog({
                  ...editDialog,
                  formData: { ...editDialog.formData, address_1: e.target.value }
                })}
              />
              <TextField
                fullWidth
                label="Address 2"
                value={editDialog.formData.address_2 || ''}
                onChange={(e) => setEditDialog({
                  ...editDialog,
                  formData: { ...editDialog.formData, address_2: e.target.value }
                })}
              />
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                  sx={{ flex: '1 1 300px' }}
                  select
                  label="Country *"
                  value={editDialog.formData.country || ''}
                  onChange={(e) => setEditDialog({
                    ...editDialog,
                    formData: { ...editDialog.formData, country: e.target.value, state: '' }
                  })}
                >
                  {getCountryOptions(editDialog.formData.country).map((country) => (
                    <MenuItem key={country} value={country}>
                      {country}
                    </MenuItem>
                  ))}
                </TextField>
                {hasPresetStates(editDialog.formData.country) ? (
                  <TextField
                    sx={{ flex: '1 1 300px' }}
                    select
                    label="State *"
                    value={editDialog.formData.state || ''}
                    onChange={(e) => setEditDialog({
                      ...editDialog,
                      formData: { ...editDialog.formData, state: e.target.value }
                    })}
                  >
                    <MenuItem value="">
                      <em>- Select -</em>
                    </MenuItem>
                    {editStateOptions.map((state) => (
                      <MenuItem key={state} value={state}>
                        {state}
                      </MenuItem>
                    ))}
                  </TextField>
                ) : (
                  <TextField
                    sx={{ flex: '1 1 300px' }}
                    label="State *"
                    value={editDialog.formData.state || ''}
                    onChange={(e) => setEditDialog({
                      ...editDialog,
                      formData: { ...editDialog.formData, state: e.target.value }
                    })}
                  />
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                  sx={{ flex: '1 1 300px' }}
                  label="City *"
                  value={editDialog.formData.city || ''}
                  onChange={(e) => setEditDialog({
                    ...editDialog,
                    formData: { ...editDialog.formData, city: e.target.value }
                  })}
                />
                <TextField
                  sx={{ flex: '1 1 300px' }}
                  label={`${getPostalCodeLabel(editDialog.formData.country)} *`}
                  value={editDialog.formData.zip_code || ''}
                  onChange={(e) => setEditDialog({
                    ...editDialog,
                    formData: { ...editDialog.formData, zip_code: e.target.value }
                  })}
                />
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setEditDialog({ open: false, firm: null, formData: {} })}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdateFirm}
            variant="contained"
            disabled={actionLoading === editDialog.firm?.id || !editDialog.formData.name || !editDialog.formData.address_1 || !editDialog.formData.city || !editDialog.formData.state || !editDialog.formData.zip_code}
          >
            {actionLoading === editDialog.firm?.id ? <CircularProgress size={16} /> : 'Update Firm'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Firms; 